import { useState, useCallback, useRef, forwardRef } from 'react';
import {
  UserPlus,
  Send,
  CheckCircle2,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import {
  validateEmail,
  validatePhone,
  validateRequired,
} from '@invitation-system/shared';
import { submitRegistration } from '../api/client';

// ─── Props ──────────────────────────────────────────────────────────────────

export interface RegistrationFormProps {
  inviterId: string;
  displayName: string;
  successQrUrl?: string;
}

// ─── Field Configuration ────────────────────────────────────────────────────

interface FieldConfig {
  key: FieldName;
  label: string;
  type: string;
  placeholder: string;
  autoComplete: string;
}

type FieldName = 'name' | 'phone' | 'email' | 'company' | 'position' | 'city';

type FormValues = Record<FieldName, string>;
type FormErrors = Partial<Record<FieldName, string>>;

const FIELDS: FieldConfig[] = [
  {
    key: 'name',
    label: '姓名',
    type: 'text',
    placeholder: '张三',
    autoComplete: 'name',
  },
  {
    key: 'phone',
    label: '手机号码',
    type: 'tel',
    placeholder: '138-0000-0000',
    autoComplete: 'tel',
  },
  {
    key: 'email',
    label: '邮箱地址',
    type: 'email',
    placeholder: 'you@example.com',
    autoComplete: 'email',
  },
  {
    key: 'company',
    label: '公司',
    type: 'text',
    placeholder: '某某科技有限公司',
    autoComplete: 'organization',
  },
  {
    key: 'position',
    label: '职位',
    type: 'text',
    placeholder: '产品经理',
    autoComplete: 'organization-title',
  },
  {
    key: 'city',
    label: '城市',
    type: 'text',
    placeholder: '上海',
    autoComplete: 'address-level2',
  },
];

const INITIAL_VALUES: FormValues = {
  name: '',
  phone: '',
  email: '',
  company: '',
  position: '',
  city: '',
};

// ─── Validation Helpers ─────────────────────────────────────────────────────

function getFieldError(field: FieldName, value: string): string | undefined {
  const trimmed = value.trim();

  if (!validateRequired(value)) {
    return `请填写${FIELDS.find((f) => f.key === field)!.label}`;
  }

  if (field === 'email' && !validateEmail(trimmed)) {
    return '请输入有效的邮箱地址（如 user@example.com）';
  }

  if (field === 'phone' && !validatePhone(value)) {
    return '手机号码只能包含数字、空格、短横线、括号或开头的加号';
  }

  return undefined;
}

// ─── RegistrationForm ───────────────────────────────────────────────────────

export function RegistrationForm({ inviterId, displayName, successQrUrl }: RegistrationFormProps) {
  const [values, setValues] = useState<FormValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Partial<Record<FieldName, boolean>>>({});
  const [submitState, setSubmitState] = useState<
    'idle' | 'submitting' | 'success' | 'error'
  >('idle');
  const [submitError, setSubmitError] = useState<string>('');
  const firstErrorRef = useRef<HTMLInputElement | null>(null);

  // Determine if any active error exists (for disabling submit)
  const hasErrors = Object.values(errors).some((e) => !!e);

  // Check if all fields have been touched and are valid for submit enablement
  const allFieldsFilled = FIELDS.every((f) => values[f.key].trim().length > 0);
  const canSubmit = allFieldsFilled && !hasErrors && submitState !== 'submitting';

  // ─── Handlers ───────────────────────────────────────────────────────────

  const handleChange = useCallback(
    (field: FieldName, value: string) => {
      setValues((prev) => ({ ...prev, [field]: value }));

      // Requirement 4.5: Phone validates LIVE while typing
      if (field === 'phone') {
        const error = value.length > 0 ? getFieldError('phone', value) : undefined;
        setErrors((prev) => ({ ...prev, phone: error }));
      }

      // Clear error for other fields when user starts typing (if previously shown)
      if (field !== 'phone' && touched[field]) {
        const error = getFieldError(field, value);
        setErrors((prev) => ({ ...prev, [field]: error }));
      }
    },
    [touched],
  );

  const handleBlur = useCallback((field: FieldName, value: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const error = getFieldError(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      // Validate all fields before submission
      const newErrors: FormErrors = {};
      let firstInvalidField: HTMLInputElement | null = null;

      for (const field of FIELDS) {
        const error = getFieldError(field.key, values[field.key]);
        if (error) {
          newErrors[field.key] = error;
          if (!firstInvalidField) {
            firstInvalidField = document.getElementById(
              `field-${field.key}`,
            ) as HTMLInputElement | null;
          }
        }
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setTouched(
          FIELDS.reduce(
            (acc, f) => ({ ...acc, [f.key]: true }),
            {} as Record<FieldName, boolean>,
          ),
        );
        // Focus first invalid field for accessibility
        firstInvalidField?.focus();
        return;
      }

      // Requirement 4.6: Trim all fields before submission
      const trimmedValues: FormValues = {
        name: values.name.trim(),
        phone: values.phone.trim(),
        email: values.email.trim(),
        company: values.company.trim(),
        position: values.position.trim(),
        city: values.city.trim(),
      };

      setSubmitState('submitting');
      setSubmitError('');

      try {
        // Requirement 5.1: POST { inviterId, ...trimmedFields } to /registrations
        await submitRegistration({
          inviterId,
          ...trimmedValues,
        });

        // Requirement 5.3: Show confirmation on 201
        setSubmitState('success');
      } catch (err: unknown) {
        // Requirement 5.4: Show error, preserve form values for retry
        setSubmitState('error');
        const e = err as { status?: number; body?: { error?: string } };
        if (e?.status === 403 && e?.body?.error === 'limit_reached') {
          setSubmitError('报名名额已满，此邀请链接已达上限。');
        } else {
          setSubmitError('提交失败，请检查网络连接后重试。');
        }
      }
    },
    [values, inviterId],
  );

  // ─── Success State ──────────────────────────────────────────────────────

  if (submitState === 'success') {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="mx-auto max-w-2xl px-6 py-12">
          <div className="bg-surface rounded-xl shadow-md p-8 text-center">
            <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-success-light">
              <CheckCircle2
                className="h-8 w-8 text-success"
                aria-hidden="true"
              />
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-3">
              报名成功
            </h2>
            <p className="text-muted leading-relaxed max-w-sm mx-auto">
              感谢您的报名！您的信息已成功提交，期待在活动中与您相见。
            </p>
            {successQrUrl && (
              <div className="mt-6">
                <p className="text-sm text-muted mb-3">请扫描下方二维码加入活动群</p>
                <img
                  src={successQrUrl}
                  alt="活动群二维码"
                  className="mx-auto max-w-[240px] rounded-lg shadow-md"
                />
              </div>
            )}
          </div>
        </main>
      </div>
    );
  }

  // ─── Form State ─────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="mx-auto max-w-2xl px-6 py-8 sm:py-12">
        <div className="bg-surface rounded-xl shadow-md p-6 sm:p-8">
          {/* Inviter attribution */}
          <div className="mb-6 pb-6 border-b border-border">
            <p className="text-muted text-sm">
              您的邀请人：{' '}
              <span className="font-semibold text-foreground">
                {displayName}
              </span>
            </p>
          </div>

          {/* Error banner for submission failures */}
          {submitState === 'error' && submitError && (
            <div
              className="mb-6 flex items-start gap-3 rounded-lg bg-error-light p-4"
              role="alert"
              aria-live="assertive"
            >
              <AlertCircle
                className="h-5 w-5 text-error shrink-0 mt-0.5"
                aria-hidden="true"
              />
              <p className="text-sm text-error font-medium">{submitError}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div className="space-y-5">
              {FIELDS.map((field) => (
                <FormField
                  key={field.key}
                  config={field}
                  value={values[field.key]}
                  error={errors[field.key]}
                  touched={!!touched[field.key]}
                  onChange={(val) => handleChange(field.key, val)}
                  onBlur={(val) => handleBlur(field.key, val)}
                  ref={
                    errors[field.key] && !firstErrorRef.current
                      ? firstErrorRef
                      : undefined
                  }
                />
              ))}
            </div>

            {/* Submit button */}
            <div className="mt-8">
              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full flex items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-white font-medium transition-colors duration-200 hover:bg-primary-dark focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary cursor-pointer"
              >
                {submitState === 'submitting' ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                    提交中…
                  </>
                ) : (
                  <>
                    <Send className="h-5 w-5" aria-hidden="true" />
                    提交报名
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────────

function Header() {
  return (
    <header className="border-b border-border bg-surface">
      <div className="mx-auto max-w-2xl px-6 py-4 flex items-center gap-3">
        <UserPlus className="h-6 w-6 text-primary" aria-hidden="true" />
        <h1 className="text-xl font-semibold text-foreground">
          活动报名
        </h1>
      </div>
    </header>
  );
}

// ─── FormField ──────────────────────────────────────────────────────────────

interface FormFieldProps {
  config: FieldConfig;
  value: string;
  error: string | undefined;
  touched: boolean;
  onChange: (value: string) => void;
  onBlur: (value: string) => void;
}

const FormField = forwardRef<HTMLInputElement, FormFieldProps>(
  function FormField({ config, value, error, touched, onChange, onBlur }, ref) {
    const fieldId = `field-${config.key}`;
    const errorId = `${fieldId}-error`;
    const showError = !!error && (touched || config.key === 'phone');

    return (
      <div>
        <label
          htmlFor={fieldId}
          className="block text-sm font-medium text-foreground mb-1.5"
        >
          {config.label}
          <span className="text-error ml-1" aria-hidden="true">
            *
          </span>
          <span className="sr-only">(required)</span>
        </label>
        <input
          ref={ref}
          id={fieldId}
          name={config.key}
          type={config.type}
          autoComplete={config.autoComplete}
          placeholder={config.placeholder}
          value={value}
          required
          aria-required="true"
          aria-invalid={showError ? 'true' : undefined}
          aria-describedby={showError ? errorId : undefined}
          onChange={(e) => onChange(e.target.value)}
          onBlur={(e) => onBlur(e.target.value)}
          className={`
            w-full rounded-lg border px-4 py-2.5 text-foreground placeholder:text-muted/60
            transition-colors duration-200
            focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary
            ${
              showError
                ? 'border-error bg-error-light/30 focus:ring-error/30 focus:border-error'
                : 'border-border bg-surface hover:border-primary-light'
            }
          `}
        />
        {showError && (
          <p
            id={errorId}
            className="mt-1.5 text-sm text-error flex items-center gap-1.5"
            role="alert"
            aria-live="polite"
          >
            <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {error}
          </p>
        )}
      </div>
    );
  },
);
